import React, { useEffect, useMemo, useState } from 'react';
import Topbar from '../components/layout/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { useTenantScope } from '../contexts/TenantScopeContext';
import { refApi, signalApi, usersApi, formatApiError } from '../lib/api';
import { SIGNAL_PRIORITIES, SIGNAL_STATUSES } from '../lib/countryConfig';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function GestionSignalements() {
  const { profile } = useAuth();
  const { country, countryCode, t } = useCountry();
  const { scopeCommuneId } = useTenantScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [communeNom, setCommuneNom] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const cId = profile?.commune_id || scopeCommuneId || undefined;
      const [sigRes, agentsRes] = await Promise.all([
        signalApi.list({ country_mode: countryCode, commune_id: cId || undefined, limit: 500 }),
        usersApi.agents().catch(() => ({ data: [] })),
      ]);
      setRows(sigRes.data || []);
      setAgents(agentsRes.data || []);
      if (cId) {
        const { data: coms } = await refApi.communes();
        setCommuneNom(coms.find((c) => c.id === cId)?.nom || '');
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [profile?.commune_id, countryCode, scopeCommuneId]);

  const patch = async (id, body) => {
    setSaving(id);
    try {
      await signalApi.update(id, body);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...body } : r)));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  // édition locale sans appel réseau (validée au blur)
  const setLocal = (id, field, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // coordonnées d'un signalement [lat, lng]
  const coordsOf = (r) => {
    if (r.geom?.coordinates) return { lng: r.geom.coordinates[0], lat: r.geom.coordinates[1] };
    if (r.latitude != null && r.longitude != null) return { lat: r.latitude, lng: r.longitude };
    return null;
  };

  // suggestions de noms d'assignation (agents + noms déjà saisis)
  const assignees = useMemo(() => {
    const set = new Set();
    agents.forEach((a) => a.full_name && set.add(a.full_name));
    rows.forEach((r) => r.assigned_name && set.add(r.assigned_name));
    return Array.from(set).sort();
  }, [agents, rows]);

  const fermer = (id) => patch(id, { statut: 'resolu' });
  const valider = (id) => patch(id, { statut: 'nouveau' });

  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const viewPhoto = async (id) => {
    setPhotoLoading(true); setPhotoUrl('loading');
    try {
      const { data } = await signalApi.photo(id);
      setPhotoUrl(data?.photo || null);
    } catch { setPhotoUrl(null); } finally { setPhotoLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter === 'open') list = list.filter((r) => r.statut !== 'resolu' && r.statut !== 'rejete');
    else if (statusFilter !== 'all') list = list.filter((r) => r.statut === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.titre || '').toLowerCase().includes(q) ||
        (r.commune_nom || '').toLowerCase().includes(q) ||
        (r.type || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, statusFilter, search]);

  const today = todayStr();
  const isOverdue = (r) => r.echeance && r.echeance < today && r.statut !== 'resolu' && r.statut !== 'rejete';
  const overdueCount = rows.filter(isOverdue).length;
  const openCount = rows.filter((r) => r.statut !== 'resolu' && r.statut !== 'rejete').length;

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="gestion-page">
      <Topbar liveBadge={false} />

      <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">📋 Gestion des signalements</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Suivi, échéances, assignation et clôture {communeNom ? `— ${t.commune} : ${communeNom}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700" data-testid="gestion-stat-open">
            {openCount} ouvert{openCount > 1 ? 's' : ''}
          </span>
          <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#FEE2E2', color: '#C1121F' }} data-testid="gestion-stat-overdue">
            ⏰ {overdueCount} en retard
          </span>
        </div>
      </div>

      <div className="px-6 py-3 bg-white border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (titre, commune, type)…"
          className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 w-72"
          data-testid="gestion-search"
        />
        <div className="flex gap-1 ml-auto">
          {[{ v: 'open', l: 'Ouverts' }, { v: 'all', l: 'Tous' }, ...SIGNAL_STATUSES.map((s) => ({ v: s.value, l: s.label }))].map((b) => (
            <button
              key={b.v}
              onClick={() => setStatusFilter(b.v)}
              className={`text-xs px-3 py-1.5 rounded-full transition ${statusFilter === b.v ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              style={statusFilter === b.v ? { background: country.palette.primary } : {}}
              data-testid={`gestion-filter-${b.v}`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>

      <main className="flex-1 overflow-auto p-6">
        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">⚠️ {error}</div>}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gestion-table">
              <thead>
                <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-semibold">Signalement</th>
                  <th className="px-3 py-3 font-semibold">Type</th>
                  <th className="px-3 py-3 font-semibold">Priorité</th>
                  <th className="px-3 py-3 font-semibold">{t.commune}</th>
                  <th className="px-3 py-3 font-semibold">Assigné à</th>
                  <th className="px-3 py-3 font-semibold">Échéance</th>
                  <th className="px-3 py-3 font-semibold">Statut</th>
                  <th className="px-3 py-3 font-semibold">Itinéraire</th>
                  <th className="px-3 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500 text-sm">Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500 text-sm" data-testid="gestion-empty">Aucun signalement</td></tr>
                ) : (
                  filtered.map((r) => {
                    const prio = SIGNAL_PRIORITIES.find((p) => p.value === r.priorite);
                    const stat = SIGNAL_STATUSES.find((p) => p.value === r.statut);
                    const overdue = isOverdue(r);
                    const closed = r.statut === 'resolu' || r.statut === 'rejete';
                    const searchQuery = encodeURIComponent(`${r.titre || ''} ${r.commune_nom || ''}`);
                    return (
                      <tr key={r.id} className={`border-t border-gray-100 hover:bg-gray-50/60 ${closed ? 'opacity-60' : ''}`} data-testid={`gestion-row-${r.id}`}>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                            {r.titre}
                            {r.source === 'citoyen' && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: '#EDE7F6', color: '#5E35B1' }} data-testid={`gestion-citoyen-${r.id}`}>👤 Citoyen</span>}
                            {r.has_photo && <button onClick={() => viewPhoto(r.id)} className="text-[10px] hover:opacity-70" title="Voir la photo" data-testid={`gestion-photo-${r.id}`}>📷</button>}
                          </div>
                          <div className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleDateString(country.labels.locale)}{r.citizen_name ? ` · ${r.citizen_name}` : ''}{r.citizen_contact ? ` · ${r.citizen_contact}` : ''}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-gray-700">{r.type}</td>
                        <td className="px-3 py-3 align-top">
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ background: `${prio?.color}22`, color: prio?.color }}>{prio?.label || r.priorite}</span>
                        </td>
                        <td className="px-3 py-3 align-top text-gray-700">{r.commune_nom || '—'}</td>
                        <td className="px-3 py-3 align-top">
                          <input
                            list="gestion-assignees"
                            value={r.assigned_name || ''}
                            onChange={(e) => setLocal(r.id, 'assigned_name', e.target.value)}
                            onBlur={(e) => patch(r.id, { assigned_name: e.target.value.trim() || null })}
                            disabled={closed}
                            placeholder="Nom de l'employé…"
                            className="text-xs px-2 py-1 rounded border border-gray-200 bg-white w-[150px]"
                            data-testid={`gestion-assign-${r.id}`}
                          />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <input
                            type="date"
                            value={r.echeance || ''}
                            onChange={(e) => patch(r.id, { echeance: e.target.value || null })}
                            disabled={closed}
                            className={`text-xs px-2 py-1 rounded border bg-white ${overdue ? 'border-red-400 text-red-700 font-semibold' : 'border-gray-200'}`}
                            data-testid={`gestion-echeance-${r.id}`}
                          />
                          {overdue && <div className="text-[10px] text-red-600 font-semibold mt-0.5" data-testid={`gestion-overdue-${r.id}`}>⏰ En retard</div>}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <select
                            value={r.statut}
                            onChange={(e) => patch(r.id, { statut: e.target.value })}
                            className="text-xs px-2 py-1 rounded border bg-white"
                            style={{ borderColor: stat?.color, color: stat?.color, fontWeight: 600 }}
                            data-testid={`gestion-statut-${r.id}`}
                          >
                            {SIGNAL_STATUSES.map((p) => <option key={p.value} value={p.value} style={{ color: '#111' }}>{p.label}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-3 align-top">
                          {(() => {
                            const c = coordsOf(r);
                            if (!c) return <span className="text-gray-300 text-xs">—</span>;
                            const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`;
                            const waze = `https://www.waze.com/ul?ll=${c.lat},${c.lng}&navigate=yes`;
                            return (
                              <div className="flex gap-1.5">
                                <a href={gmaps} target="_blank" rel="noopener noreferrer" title="Itinéraire Google Maps" className="text-[10px] font-semibold px-2 py-1 rounded-md text-white hover:opacity-90 transition" style={{ background: '#1A73E8' }} data-testid={`gestion-gmaps-${r.id}`}>Maps</a>
                                <a href={waze} target="_blank" rel="noopener noreferrer" title="Itinéraire Waze" className="text-[10px] font-semibold px-2 py-1 rounded-md text-white hover:opacity-90 transition" style={{ background: '#33CCFF' }} data-testid={`gestion-waze-${r.id}`}>Waze</a>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-3 align-top text-right space-x-1 whitespace-nowrap">
                          {r.statut === 'a_valider' && (
                            <button
                              onClick={() => valider(r.id)}
                              disabled={saving === r.id}
                              className="text-xs px-3 py-1.5 rounded-lg text-white font-medium transition disabled:opacity-50"
                              style={{ background: '#5E35B1' }}
                              data-testid={`gestion-valider-${r.id}`}
                            >
                              ✓ Valider
                            </button>
                          )}
                          {closed ? (
                            <span className="text-[11px] text-gray-400">Clôturé</span>
                          ) : (
                            <button
                              onClick={() => fermer(r.id)}
                              disabled={saving === r.id}
                              className="text-xs px-3 py-1.5 rounded-lg text-white font-medium transition disabled:opacity-50"
                              style={{ background: country.palette.success }}
                              data-testid={`gestion-fermer-${r.id}`}
                            >
                              ✓ Fermer
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <datalist id="gestion-assignees">
              {assignees.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-3">
          ℹ️ Un signalement « Résolu » est automatiquement retiré de la carte. Les signalements « 👤 Citoyen » arrivent en « À valider ».
        </p>
      </main>

      {photoUrl !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4" onClick={() => setPhotoUrl(null)} data-testid="gestion-photo-modal">
          <div className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            {photoLoading || photoUrl === 'loading' ? (
              <div className="text-white text-center">Chargement…</div>
            ) : photoUrl ? (
              <img src={photoUrl} alt="Signalement" className="rounded-xl w-full" />
            ) : (
              <div className="text-white text-center">Aucune photo disponible.</div>
            )}
            <button onClick={() => setPhotoUrl(null)} className="mt-3 mx-auto block text-xs px-4 py-2 rounded-lg bg-white/90 text-gray-900" data-testid="gestion-photo-close">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
